import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ErrorBox } from "../../components/ui";
import { useRequisitions, useUpcomingInterviews } from "../../lib/api";
import { RequisitionTable } from "../recruitment/RequestPosition";
import { SectionCard } from "./shared";
import { hhmm } from "../../lib/format";

/** What the hiring screen could not show before: every resourcing request and where it stands, and the interviews
 * booked from today with who has scored. */
export function RequestsSection({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useRequisitions(company || undefined);
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const d = q.data;
  const aside = Object.entries(d.counts).filter(([, n]) => n).map(([s, n]) => `${t(`req.status.${s}`, s)} ${n}`).join(" · ");
  return (
    <SectionCard title={t("req.listTitle")} aside={aside || t("req.none")}>
      <div className="flex items-center justify-between px-4 pt-2 text-xs">
        <span className="text-brand-grey">{t("req.listHint")}</span>
        <Link to="/people/recruitment/request" className="font-semibold text-brand-primary hover:underline" data-req-ask>{t("req.ask")} →</Link>
      </div>
      <div className="px-2 pb-2" data-req-list>{d.rows.length ? <RequisitionTable rows={d.rows} /> : <div className="p-4 text-center text-xs text-brand-grey">{t("req.none")}</div>}</div>
    </SectionCard>
  );
}

export function UpcomingInterviews({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useUpcomingInterviews(company || undefined);
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const rows = q.data.rows;
  return (
    <SectionCard title={t("iv.upcomingTitle")} aside={t("iv.count", { n: rows.length })}>
      <div data-iv-upcoming>
        {rows.length === 0 && <div className="p-4 text-center text-xs text-brand-grey">{t("iv.noneUpcoming")}</div>}
        {rows.length > 0 && (
          <table className="w-full text-sm"><tbody>{rows.map((r) => (
            <tr key={r.name} className="border-t border-brand-grey-light" data-iv-upcoming-row={r.name}>
              <td className="px-4 py-2"><div className="font-semibold">{r.applicant_name}</div><div className="text-[11px] text-brand-grey">{r.opening_title || r.designation} · {r.company}</div></td>
              <td className="px-2 py-2 text-xs"><b>{r.scheduled_on}</b> · {hhmm(r.from_time)}–{hhmm(r.to_time)}</td>
              <td className="px-2 py-2 text-xs">{r.panel.filter(Boolean).join(", ")}<div className="text-[11px] text-brand-grey">{t("iv.reading", { answered: r.answered, of: r.of })}</div></td>
              <td className="px-4 py-2 text-right text-xs">{r.my_turn && <span className="mr-2 rounded bg-blue-50 px-1.5 py-0.5 font-semibold text-brand-primary">{t("iv.yourTurn")}</span>}
                {r.evaluation && r.job_opening && <Link to={`/people/recruitment/${encodeURIComponent(r.job_opening)}/${encodeURIComponent(r.evaluation)}`} className="text-brand-primary hover:underline">{t("iv.open")}</Link>}</td>
            </tr>))}</tbody></table>
        )}
      </div>
    </SectionCard>
  );
}
